import { InputHTMLAttributes, forwardRef } from 'react';
import { Box, Text, chakra } from '@chakra-ui/react';
import { Eye, EyeOff } from 'lucide-react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  showPasswordToggle?: boolean;
  onPasswordToggle?: () => void;
  showPassword?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      showPasswordToggle = false,
      onPasswordToggle,
      showPassword = false,
      type = 'text',
      ...props
    },
    ref
  ) => {
    const inputType = showPasswordToggle && type === 'password' ? (showPassword ? 'text' : 'password') : type;

    return (
      <Box w="full">
        {label && (
          <chakra.label display="block" mb="2" fontSize="sm" fontWeight="medium" color="volyx.text">
            {label}
          </chakra.label>
        )}
        <Box position="relative" w="full">
          <chakra.input
            ref={ref}
            type={inputType}
            w="full"
            h="14"
            bg="volyx.lightGray"
            borderRadius="xl"
            border={error ? '1px solid' : 'none'}
            borderColor={error ? 'volyx.error' : undefined}
            px="4"
            pr={showPasswordToggle && type === 'password' ? '50px' : undefined}
            fontSize="md"
            fontFamily="body"
            color="volyx.text"
            boxShadow="0 1px 3px rgba(0,0,0,0.08)"
            transition="all 0.2s"
            _focus={{ outline: 'none', boxShadow: '0 0 0 3px rgba(31,41,50,0.1)', transform: 'scale(1.01)' }}
            _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
            {...props}
          />
          {showPasswordToggle && (
            <chakra.button
              type="button"
              position="absolute"
              right="4"
              top="50%"
              zIndex={1}
              transform="translateY(-50%)"
              bg="transparent"
              border="none"
              p="2"
              cursor="pointer"
              display="flex"
              alignItems="center"
              justifyContent="center"
              borderRadius="md"
              color="volyx.textSecondary"
              transition="colors 0.2s"
              _hover={{ bg: 'rgba(0,0,0,0.05)' }}
              _focus={{ outline: 'none', bg: 'rgba(0,0,0,0.1)' }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onPasswordToggle?.();
              }}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? (
                <EyeOff size={20} stroke="currentColor" />
              ) : (
                <Eye size={20} stroke="currentColor" />
              )}
            </chakra.button>
          )}
        </Box>
        {error && (
          <Text as="span" display="block" mt="1" fontSize="xs" color="volyx.error" fontFamily="body">
            {error}
          </Text>
        )}
      </Box>
    );
  }
);

Input.displayName = 'Input';
